import { createActionCreator, createReducer } from 'deox';
import { isNil } from 'ramda';
import { AppState } from 'types/AppState';
import { Revision, Revisions, Snapshot } from 'types/Revision';
import { createSelector } from 'reselect';
import { Effects } from 'types/Effects';
import { Scenes } from 'types/Scene';
import { Audio, UserSongs } from 'types/Audio';
import { RatioFormatType } from 'types/RatioFormat';
import { SceneVolume } from 'types/sceneVolume';
import { getEffects } from 'modules/effects';
import { getAudio } from 'modules/audio';
import { getRatioFormat } from 'modules/ratioFormat';
import { getSceneVolume } from 'modules/sceneVolume';
import produce, { Draft } from 'immer';
import { ProjectFileBase } from 'types/ProjectFiles';
import { getCurrentProjectName } from 'modules/projectFles';

const MAX_REVISION_COUNT = 50;

const prefix = 'REVISION';
export const defaultRevisionsState: Revisions = {
  offset: 0,
  items: []
};

export const saveRevision = createActionCreator(
  `${prefix}/SAVE`,
  resolve => (tool: Revision['tool']) => resolve(tool)
);

export const addRevision = createActionCreator(`${prefix}/ADD`, resolve => (revision: Revision) =>
  resolve(revision)
);

export const decrementRevisionOffset = createActionCreator(`${prefix}/DECREMENT_OFFSET`);
export const incrementRevisionOffset = createActionCreator(`${prefix}/INCREMENT_OFFSET`);

export const applyPreviousRevision = createActionCreator(`${prefix}/APPLY_PREVIOUS`);
export const applyNextRevision = createActionCreator(`${prefix}/APPLY_NEXT`);

export const updateRevisions = createActionCreator(
  `${prefix}/UPDATE`,
  resolve => (payload: Partial<Revisions>) => resolve(payload)
);

export const applyRevision = createActionCreator(
  `${prefix}/APPLY`,
  resolve => (revision: Revision) => resolve({ revision })
);

export const revisions = createReducer(defaultRevisionsState, handle => [
  handle(
    addRevision,
    produce<(d: Draft<Revisions>, a: ReturnType<typeof addRevision>) => void>(
      (draft, { payload: revision }) => {
        if (draft.offset > 0) {
          const items = draft.items;

          draft.items = [...items.slice(0, items.length - draft.offset), revision];
          draft.offset = 0;
        } else {
          draft.items.push(revision);
        }

        if (draft.items.length > MAX_REVISION_COUNT) {
          draft.items = draft.items.slice(draft.items.length - MAX_REVISION_COUNT);
        }
      }
    )
  ),
  handle(updateRevisions, (state, { payload }) => ({
    offset: payload.offset ?? state.offset,
    items: payload.items ?? state.items
  })),
  handle(incrementRevisionOffset, state => ({
    offset: state.offset + 1,
    items: state.items
  })),
  handle(decrementRevisionOffset, state => ({
    offset: state.offset - 1,
    items: state.items
  }))
]);

export const getRevisions = (state: AppState): Revision[] => state.revisions.items;

export const getRevisionsOffset = (state: AppState): Revisions['offset'] => state.revisions.offset;

export const getSnapshot = createSelector<
  AppState,
  Scenes,
  Effects,
  Audio,
  UserSongs,
  RatioFormatType,
  SceneVolume,
  ProjectFileBase['name'],
  Snapshot
>(
  state => state.scenes,
  getEffects,
  getAudio,
  state => state.userSongs,
  getRatioFormat,
  getSceneVolume,
  getCurrentProjectName,
  (scenes, effects, audio, userSongs, ratioFormat, sceneVolume, projectName) => ({
    scenes,
    effects,
    audio,
    userSongs,
    ratioFormat,
    sceneVolume,
    projectName
  })
);

export const getRevision = (offset: Revisions['offset']) => (
  state: AppState
): Revision | undefined => {
  const items = getRevisions(state);
  return items[items.length - offset - 1];
};

export const getPreviousRevision = (state: AppState): Revision | null => {
  const offset = getRevisionsOffset(state);
  return getRevision(offset + 1)(state);
};

export const getNextRevision = (state: AppState): Revision | null => {
  const offset = getRevisionsOffset(state);
  return getRevision(offset - 1)(state);
};

export const getCurrentRevision = (state: AppState): Revision => {
  const offset = getRevisionsOffset(state);
  return getRevision(offset)(state);
};

export const isPrevRevisionExists = (state: AppState): boolean =>
  !isNil(getPreviousRevision(state));

export const isNextRevisionExists = (state: AppState): boolean => !isNil(getNextRevision(state));
